import type { IBaseComponentProps } from 'components/base/types'
import type { IBubbletipProps } from '../types'
import { computed, reactive, ref, watch, type ComputedRef, type Ref } from 'vue'
import { useElementBounding, useElementSize, useWindowSize } from '@vueuse/core'
import { useEvent } from '@/utils/hooks/useEvent'
import { useMouse } from '@/utils/mouse'
import { clamp, lerp, mix, pointVSAABB, smoothstep, VEC2, VEC4, type AABB, type Vector } from 'math'
import { useEffect } from '@/utils/reactive/useEffect'
import { unwrapElementRef } from '@/utils/dom/unwrap'
import { getElementAABB } from '@/types/aabb'
import { isSVGGeometryElement } from '@/utils/is'
import { findSVGElement, getPositionInSVG } from '@/utils/dom/svg'
import { uref } from '@/utils/reactive/uref'
import { useCanceledTimeout } from '@/utils/hooks/useCanceledTimeout'
import { useAnimationStep, type UseAnimationStep } from '@/utils/animation/useAnimationStep'
import { unref } from 'vue'

const ANIMATION_DURATION = 0.45;

export type UseTooltipProps = Omit<IBubbletipProps, keyof IBaseComponentProps> & {
  getOthers?: () => UseTooltip[];
}

export type UseTooltip = {
  anim: UseAnimationStep;
  tipDir: ComputedRef<Vector>
  targetPosition: ComputedRef<Vector>
  pos: ComputedRef<Vector>
  tipPos: ComputedRef<Vector>
  tipLocalDir: ComputedRef<Vector>
  animDir: Ref<number>
  cornerFactor: ComputedRef<number>
  show: () => void
  hide: () => void
  tipStops: ComputedRef<Vector>
  tipStopsSigned: ComputedRef<Vector>
  triggerRef: Ref;
  update: () => void;
}

export const useTooltip = (toolref: Ref, props: UseTooltipProps): UseTooltip => {
  const outputVisibility = props.outputVisibility
  const triggerSize = useElementSize(props.triggerRef)
  const toolSize = useElementSize(toolref)
  const triggerBound = useElementBounding(props.triggerRef)
  const toolBound = useElementBounding(toolref)
  const mouse = useMouse()
  const windowInfo = useWindowSize();

  const anim = useAnimationStep({
    initial: 0.0,
    duration: ANIMATION_DURATION,
    curve: (start, target, mix) => lerp(start, target, smoothstep(0.0, 0.75, mix))
  });

  const animDir = ref<number>(1)
  const state = reactive<{
    isOnTool: boolean
    isOnTrigger: boolean
  }>({
    isOnTool: false,
    isOnTrigger: false
  })

  const handleMouseEnter = () => {
    const ignore = props.ignore || []

    for (const ig of ignore) {
      const el = unwrapElementRef(ig)
      if (!el) continue
      const bounds = getElementAABB(el)

      if (isSVGGeometryElement(el)) {
        const svg = findSVGElement(el)
        if (svg) {
          const point = getPositionInSVG(svg, mouse.mouse.position)
          if (el.isPointInFill(point)) {
            hide()
            return
          }
        }
        break
      }

      if (pointVSAABB(mouse.mouse.position, bounds)) {
        hide()
        return
      }
    }

    state.isOnTrigger = true
  }


  const handleMouseLeave = async () => {
    if (props.active) return
    state.isOnTrigger = false
  }

  const handleMouseEnterTool = () => {
    if (anim.current.value < 0.5) return;
    state.isOnTool = true
  }

  const handleMouseLeaveTool = async () => {
    state.isOnTool = false;
  }

  const shouldBeVisible = computed(() => (state.isOnTrigger || (props.hoverable && state.isOnTool) || false));

  const hideTimer = useCanceledTimeout();

  watch(shouldBeVisible, (vis) => {
    if (vis) {
      hideTimer.cancel();
      show();
    } else {
      hideTimer.setTimeout(() => {
        hide();
      }, 120);
    }
  })


  if (outputVisibility) {
    useEffect(() => {
      outputVisibility.value = anim.current.value
    }, [() => anim.current.value])
  }


  watch(props.triggerRef, (it) => {
    if (props.noMouseEvents) return
    const el = unwrapElementRef(it)
    if (!el) return

    el.addEventListener('mouseenter', handleMouseEnter)
    el.addEventListener('mouseleave', handleMouseLeave)
  }, { immediate: true, deep: true })

  useEffect(() => {
    const el = unwrapElementRef(toolref)
    if (!el) return
    el.onmouseenter = handleMouseEnterTool
    el.onmouseleave = handleMouseLeaveTool
  }, [toolref])

  const tipSize = computed((): Vector => {
    const s = props.size === 's' ? 9 : 16
    return VEC2(s, s)
  })

  if (props.hoverable) {
    useEvent('mousemove', () => {
      triggerBound.update()
      toolBound.update()
    })
  }



  const tipDir = computed(() => {

    if (props.placement === 'auto') {
      const refP = uref(props.referencePoint);
      const targetP = unref(props.targetPosition);

      if (refP && targetP) {
        const trigP = VEC2(triggerBound.x.value, triggerBound.y.value);
        const pA = refP.sub(trigP);
        const pB = targetP.sub(trigP);
        return pA.sub(pB).unit();
      }
    }

    // if (props.targetPosition) return VEC2(0, 0)
    switch (props.placement) {
      case 'middle':
        return VEC2(0, 0)
      case 'mouse':
        return VEC2(0, 0)
      case 'top-right':
      case 'right':
        return VEC2(-1, 0)
      case 'bottom':
        return VEC2(0, -1)
      case 'input-field':
        return VEC2(0, 1);
      default:
        return VEC2(0, 1)
    }
  })

  const spacing = computed(() => props.spacing ?? 8)

  const show = () => {
    update()
    anim.to(1.0, ANIMATION_DURATION);
    animDir.value = 1
  }

  const hide = () => {
    if (props.active) return
    update()
    anim.to(0.0, ANIMATION_DURATION);
    animDir.value = -1
  }

  useEffect(() => {
    if (props.visible === true) {
      handleMouseEnter()
    } else if (props.visible === false) {
      handleMouseLeave()
    }
  }, [() => props.visible, () => unref(props.targetPosition)])

  // useEffect(() => {
  //   if (!props.targetPosition) return
  //   show()
  // }, [() => props.targetPosition])

  const targetPosition = computed(() => {
    triggerBound.update()
    toolBound.update();

    if (props.placement === 'input-field') {
      const A: AABB = {
        min: VEC2(0, 0),
        max: VEC2(toolBound.width.value, toolBound.height.value)
      };

      const B: AABB = {
        min: VEC2(triggerBound.x.value, triggerBound.y.value),
        max: VEC2(triggerBound.x.value + triggerBound.width.value, triggerBound.y.value + triggerBound.height.value)
      };

      const SA = A.max.sub(A.min);
      const SB = B.max.sub(B.min);

      const p = B.min.add(SB.scale(0.5)).sub(SA.scale(0.5)).sub(VEC2(0, SB.y * 2));
      return VEC2(p.x, p.y + spacing.value);
    }

    if (props.targetPosition && props.referencePoint) {
      const isTie = props.tipAppearance?.shape === 'tie'
      const radi = Math.max(toolBound.width.value, toolBound.height.value)
      const dir = uref(props.referencePoint).sub(unref(props.targetPosition) || VEC2(0, 0)).unit();
      const tipdir = dir;
      const tsize = VEC2(toolBound.width.value, toolBound.height.value);
      const dirSize = (0.5 * mix(tsize.x, tsize.y, Math.abs(tipdir.dot(VEC2(0, 1))))) + tipSize.value.mag() + spacing.value;
      const offset = isTie ? radi * 0.5 + 16 + spacing.value : dirSize;
      return (unref(props.targetPosition) || VEC2(0, 0))
                  .sub(VEC2(toolBound.width.value, toolBound.height.value).scale(0.5))
                  .sub(tipdir.scale(offset))
    }

    const trigEl = unwrapElementRef(props.triggerRef)
    const rect = trigEl ? trigEl.getBoundingClientRect() : null

    const trigSize = VEC2(
      rect?.width ?? triggerSize.width.value,
      rect?.height ?? triggerSize.height.value
    )
    const trigPos = VEC2(
      rect?.x ?? triggerBound.x.value,
      rect?.y ?? triggerBound.y.value
    )

    const trigCent = trigPos.add(trigSize.scale(0.5))
    const tSize = VEC2(toolBound.width.value, toolBound.height.value)
    const placeDir = tipDir.value.scale(-1)
    const f = anim.current.value;//anim.current.value
    const af = Math.sin(f * Math.PI) * 2 * f * f

    if (props.placement === 'mouse') return mouse.mouse.position

    return trigCent
      .sub(tSize.scale(0.5))
      .add(placeDir.mul(trigSize.add(tSize).scale(0.5)))
      .add(placeDir.scale(spacing.value))
      .add(placeDir.scale(af * spacing.value))
  })

  const overflow = computed(() => {
    const p = targetPosition.value
    const windowW = windowInfo.width.value
    const windowH = windowInfo.height.value
    const toolRight = p.x + toolSize.width.value
    const right = Math.max(0, toolRight + 2 * 16 + spacing.value - windowW)
    const toolBottom = p.y + toolSize.height.value
    const bottom = Math.max(0, toolBottom + 2 * 16 + spacing.value - windowH)
    const toolTop = p.y
    const top = Math.max(0, -toolTop)

    return {
      right,
      bottom,
      top
    }
  })

  const pos = computed(() => {
    const p = targetPosition.value
    const oflow = overflow.value
    return VEC2(p.x - oflow.right, p.y - oflow.bottom + oflow.top)
  })

  const tipPos = computed(() => {
    const trigEl = unwrapElementRef(props.triggerRef)
    if (!trigEl) return VEC2(0, 0)
    const trigRect = trigEl.getBoundingClientRect()
    const toolEl = unwrapElementRef(toolref)
    if (!toolEl) return VEC2(0, 0)
    const toolRect = toolEl.getBoundingClientRect()


    const tpos = props.targetPosition
      ? (unref(props.targetPosition) || VEC2(0, 0))
      : VEC2(trigRect.x, trigRect.y).add(VEC2(trigRect.width, trigRect.height).scale(0.5))

    //const ofScale = (props.size === 's' || props.noPadding) ? 1.5 : 1;
    //const tt = Math.max(tipSize.value.x, tipSize.value.y);
    //const ofX = 0.5 * tipSize.value.x * ofScale;
    //const ofY = 0.5 * tipSize.value.y * ofScale;

    return tpos

      .sub(tipSize.value.scale(0.5))
      .clamp(
        VEC2(pos.value.x + 6, pos.value.y),
        VEC2(
          pos.value.x + toolRect.width - (tipSize.value.x + 6),
          pos.value.y + toolRect.height - tipSize.value.y
        )
      )
      .add(tipDir.value.scale(Math.max(tipSize.value.x, tipSize.value.y) * 0.3333333))

    //return tpos

    //  .sub(tipSize.value.scale(0.5))
    //  .clamp(
    //    VEC2(pos.value.x, pos.value.y),
    //    VEC2(pos.value.x + toolRect.width - ofX , pos.value.y + toolRect.height - ofY)
    //  )
    //  .add(tipDir.value.scale(tt * 0.45))
  })

  const tipStopsSigned = computed(() => {
    const trigEl = unwrapElementRef(props.triggerRef)
    if (!trigEl) return VEC4(0, 0, 0, 0)
    const toolEl = unwrapElementRef(toolref)
    if (!toolEl) return VEC4(0, 0, 0, 0)
    const toolRect = toolEl.getBoundingClientRect()

    const toolsiz = VEC2(toolRect.width, toolRect.height)
    const tip = tipPos.value
    const toolCenter = pos.value.add(toolsiz.scale(0.5))

    const w = Math.max(1, toolsiz.x)
    const h = Math.max(1, toolsiz.y)
    const x = clamp((toolCenter.x - (tip.x + 0.5 * tipSize.value.x)) / (w * 0.5), -1, 1)
    const y = clamp((toolCenter.y - (tip.y + 0.5 * tipSize.value.y)) / (h * 0.5), -1, 1)
    const left = x
    const right = -x
    const top = y
    const bottom = -y

    return VEC4(left, right, top, bottom)
  })

  const tipStops = computed(() => {
    const left = Math.max(0, tipStopsSigned.value.x)
    const right = Math.max(0, tipStopsSigned.value.y)
    const top = Math.max(0, tipStopsSigned.value.z)
    const bottom = Math.max(0, tipStopsSigned.value.w)
    return VEC4(left, right, top, bottom)
  })

  const cornerFactor = computed(() => {
    const stops = tipStops.value
    return clamp(
      Math.max(
        Math.abs(stops.x * stops.z),
        Math.abs(stops.y * stops.z),
        Math.abs(stops.x * stops.w),
        Math.abs(stops.y * stops.w)
      ),
      0,
      1
    )
  })

  const tipLocalDir = computed(() => {
    const trigEl = unwrapElementRef(props.triggerRef)
    if (!trigEl) return VEC2(0, 0)
    const toolEl = unwrapElementRef(toolref)
    if (!toolEl) return VEC2(0, 0)
    const toolRect = toolEl.getBoundingClientRect()
    const toolsiz = VEC2(toolRect.width, toolRect.height)
    const tip = tipPos.value
    const toolCenter = pos.value.add(toolsiz.scale(0.5))
    const w = Math.max(1, toolsiz.x)
    const h = Math.max(1, toolsiz.y)
    const x = (toolCenter.x - tip.x) / (w * 0.5)
    const y = (toolCenter.y - tip.y) / (h * 0.5)
    return VEC2(x, y).unit()
  });

  const update = () => {
    triggerBound.update();
    toolBound.update();
  }

  return {
    anim,
    show,
    hide,
    tipDir,
    targetPosition,
    pos,
    tipPos,
    animDir,
    tipStops,
    tipStopsSigned,
    cornerFactor,
    tipLocalDir,
    triggerRef: props.triggerRef,
    update
  }
}
